import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  useMediaQuery,
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { Check } from '@mui/icons-material';
import ACSnackbar from '../../ACSnackbar';
import AdvisoryCloudLogo from '../../AdvisoryCloudLogo2';
import { useAccount } from '../../../contexts/Account';
import { freemiumUpgradeCompany } from '../../../services/advisor_services';
import { SnackbarType } from '../../../services/utils/types';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import Arrow from './arrow.png';
import { useBoardOwner } from '../../../hooks/useBoardOwner';
import { updateAttributesFreemiumUpgrade } from '../../../services/company_services';
import {
  sendFSEventOnUpgrade,
  sendFSEventUpgradeViewed,
} from '../../../services/FullStory';
import {
  cardStyles,
  formatBillingCycle,
  formatPrice,
  buildFeatures,
  planFeatureFactories,
  planTagMap,
  planAccentMap,
} from './Step1';
import './freemiumStyles.css';

const errorCodes = {
  validationError: 'VALIDATION_ERROR',
  recurlyError: 'RECURLY_ERROR',
  inAppError: 'IN_APP_ERROR',
  salesforceError: 'SALESFORCE_ERROR',
};

const AP_INIT_KEY = '__AC_APPLEPAY_INIT_COMPANY__';

const INITIAL_MODEL = {
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  acceptTermsOfService: false,
};

const countries = [
  { text: 'United States', value: 'US' },
  { text: 'Afghanistan', value: 'AF' },
  { text: 'Åland Islands', value: 'AX' },
  { text: 'Albania', value: 'AL' },
  { text: 'Algeria', value: 'DZ' },
  { text: 'American Samoa', value: 'AS' },
  { text: 'Andorra', value: 'AD' },
  { text: 'Angola', value: 'AO' },
  { text: 'Anguilla', value: 'AI' },
  { text: 'Antarctica', value: 'AQ' },
  { text: 'Antigua and Barbuda', value: 'AG' },
  { text: 'Argentina', value: 'AR' },
  { text: 'Armenia', value: 'AM' },
  { text: 'Aruba', value: 'AW' },
  { text: 'Australia', value: 'AU' },
  { text: 'Austria', value: 'AT' },
  { text: 'Azerbaijan', value: 'AZ' },
  { text: 'Bahamas', value: 'BS' },
  { text: 'Bahrain', value: 'BH' },
  { text: 'Bangladesh', value: 'BD' },
  { text: 'Barbados', value: 'BB' },
  { text: 'Belarus', value: 'BY' },
  { text: 'Belgium', value: 'BE' },
  { text: 'Belize', value: 'BZ' },
  { text: 'Benin', value: 'BJ' },
  { text: 'Bermuda', value: 'BM' },
  { text: 'Bhutan', value: 'BT' },
  { text: 'Bolivia', value: 'BO' },
  { text: 'Bosnia and Herzegovina', value: 'BA' },
  { text: 'Botswana', value: 'BW' },
  { text: 'Bouvet Island', value: 'BV' },
  { text: 'Brazil', value: 'BR' },
  { text: 'British Indian Ocean Territory', value: 'IO' },
  { text: 'Brunei Darussalam', value: 'BN' },
  { text: 'Bulgaria', value: 'BG' },
  { text: 'Burkina Faso', value: 'BF' },
  { text: 'Burundi', value: 'BI' },
  { text: 'Cambodia', value: 'KH' },
  { text: 'Cameroon', value: 'CM' },
  { text: 'Canada', value: 'CA' },
  { text: 'Cape Verde', value: 'CV' },
  { text: 'Cayman Islands', value: 'KY' },
  { text: 'Central African Republic', value: 'CF' },
  { text: 'Chad', value: 'TD' },
  { text: 'Chile', value: 'CL' },
  { text: 'China', value: 'CN' },
  { text: 'Christmas Island', value: 'CX' },
  { text: 'Cocos (Keeling) Islands', value: 'CC' },
  { text: 'Colombia', value: 'CO' },
  { text: 'Comoros', value: 'KM' },
  { text: 'Congo', value: 'CG' },
  { text: 'Congo, The Democratic Republic of the', value: 'CD' },
  { text: 'Cook Islands', value: 'CK' },
  { text: 'Costa Rica', value: 'CR' },
  { text: "Cote D'Ivoire", value: 'CI' },
  { text: 'Croatia', value: 'HR' },
  { text: 'Cuba', value: 'CU' },
  { text: 'Cyprus', value: 'CY' },
  { text: 'Czech Republic', value: 'CZ' },
  { text: 'Denmark', value: 'DK' },
  { text: 'Djibouti', value: 'DJ' },
  { text: 'Dominica', value: 'DM' },
  { text: 'Dominican Republic', value: 'DO' },
  { text: 'Ecuador', value: 'EC' },
  { text: 'Egypt', value: 'EG' },
  { text: 'El Salvador', value: 'SV' },
  { text: 'Equatorial Guinea', value: 'GQ' },
  { text: 'Eritrea', value: 'ER' },
  { text: 'Estonia', value: 'EE' },
  { text: 'Ethiopia', value: 'ET' },
  { text: 'Falkland Islands (Malvinas)', value: 'FK' },
  { text: 'Faroe Islands', value: 'FO' },
  { text: 'Fiji', value: 'FJ' },
  { text: 'Finland', value: 'FI' },
  { text: 'France', value: 'FR' },
  { text: 'French Guiana', value: 'GF' },
  { text: 'French Polynesia', value: 'PF' },
  { text: 'French Southern Territories', value: 'TF' },
  { text: 'Gabon', value: 'GA' },
  { text: 'Gambia', value: 'GM' },
  { text: 'Georgia', value: 'GE' },
  { text: 'Germany', value: 'DE' },
  { text: 'Ghana', value: 'GH' },
  { text: 'Gibraltar', value: 'GI' },
  { text: 'Greece', value: 'GR' },
  { text: 'Greenland', value: 'GL' },
  { text: 'Grenada', value: 'GD' },
  { text: 'Guadeloupe', value: 'GP' },
  { text: 'Guam', value: 'GU' },
  { text: 'Guatemala', value: 'GT' },
  { text: 'Guernsey', value: 'GG' },
  { text: 'Guinea', value: 'GN' },
  { text: 'Guinea-Bissau', value: 'GW' },
  { text: 'Guyana', value: 'GY' },
  { text: 'Haiti', value: 'HT' },
  { text: 'Heard Island and Mcdonald Islands', value: 'HM' },
  { text: 'Holy See (Vatican City State)', value: 'VA' },
  { text: 'Honduras', value: 'HN' },
  { text: 'Hong Kong', value: 'HK' },
  { text: 'Hungary', value: 'HU' },
  { text: 'Iceland', value: 'IS' },
  { text: 'India', value: 'IN' },
  { text: 'Indonesia', value: 'ID' },
  { text: 'Iran, Islamic Republic Of', value: 'IR' },
  { text: 'Iraq', value: 'IQ' },
  { text: 'Ireland', value: 'IE' },
  { text: 'Isle of Man', value: 'IM' },
  { text: 'Israel', value: 'IL' },
  { text: 'Italy', value: 'IT' },
  { text: 'Jamaica', value: 'JM' },
  { text: 'Japan', value: 'JP' },
  { text: 'Jersey', value: 'JE' },
  { text: 'Jordan', value: 'JO' },
  { text: 'Kazakhstan', value: 'KZ' },
  { text: 'Kenya', value: 'KE' },
  { text: 'Kiribati', value: 'KI' },
  { text: "Korea, Democratic People'S Republic of", value: 'KP' },
  { text: 'Korea, Republic of', value: 'KR' },
  { text: 'Kuwait', value: 'KW' },
  { text: 'Kyrgyzstan', value: 'KG' },
  { text: "Lao People'S Democratic Republic", value: 'LA' },
  { text: 'Latvia', value: 'LV' },
  { text: 'Lebanon', value: 'LB' },
  { text: 'Lesotho', value: 'LS' },
  { text: 'Liberia', value: 'LR' },
  { text: 'Libyan Arab Jamahiriya', value: 'LY' },
  { text: 'Liechtenstein', value: 'LI' },
  { text: 'Lithuania', value: 'LT' },
  { text: 'Luxembourg', value: 'LU' },
  { text: 'Macao', value: 'MO' },
  { text: 'Macedonia, The Former Yugoslav Republic of', value: 'MK' },
  { text: 'Madagascar', value: 'MG' },
  { text: 'Malawi', value: 'MW' },
  { text: 'Malaysia', value: 'MY' },
  { text: 'Maldives', value: 'MV' },
  { text: 'Mali', value: 'ML' },
  { text: 'Malta', value: 'MT' },
  { text: 'Marshall Islands', value: 'MH' },
  { text: 'Martinique', value: 'MQ' },
  { text: 'Mauritania', value: 'MR' },
  { text: 'Mauritius', value: 'MU' },
  { text: 'Mayotte', value: 'YT' },
  { text: 'Mexico', value: 'MX' },
  { text: 'Micronesia, Federated States of', value: 'FM' },
  { text: 'Moldova, Republic of', value: 'MD' },
  { text: 'Monaco', value: 'MC' },
  { text: 'Mongolia', value: 'MN' },
  { text: 'Montserrat', value: 'MS' },
  { text: 'Morocco', value: 'MA' },
  { text: 'Mozambique', value: 'MZ' },
  { text: 'Myanmar', value: 'MM' },
  { text: 'Namibia', value: 'NA' },
  { text: 'Nauru', value: 'NR' },
  { text: 'Nepal', value: 'NP' },
  { text: 'Netherlands', value: 'NL' },
  { text: 'Netherlands Antilles', value: 'AN' },
  { text: 'New Caledonia', value: 'NC' },
  { text: 'New Zealand', value: 'NZ' },
  { text: 'Nicaragua', value: 'NI' },
  { text: 'Niger', value: 'NE' },
  { text: 'Nigeria', value: 'NG' },
  { text: 'Niue', value: 'NU' },
  { text: 'Norfolk Island', value: 'NF' },
  { text: 'Northern Mariana Islands', value: 'MP' },
  { text: 'Norway', value: 'NO' },
  { text: 'Oman', value: 'OM' },
  { text: 'Pakistan', value: 'PK' },
  { text: 'Palau', value: 'PW' },
  { text: 'Palestinian Territory, Occupied', value: 'PS' },
  { text: 'Panama', value: 'PA' },
  { text: 'Papua New Guinea', value: 'PG' },
  { text: 'Paraguay', value: 'PY' },
  { text: 'Peru', value: 'PE' },
  { text: 'Philippines', value: 'PH' },
  { text: 'Pitcairn', value: 'PN' },
  { text: 'Poland', value: 'PL' },
  { text: 'Portugal', value: 'PT' },
  { text: 'Puerto Rico', value: 'PR' },
  { text: 'Qatar', value: 'QA' },
  { text: 'Reunion', value: 'RE' },
  { text: 'Romania', value: 'RO' },
  { text: 'Russian Federation', value: 'RU' },
  { text: 'Rwanda', value: 'RW' },
  { text: 'Saint Helena', value: 'SH' },
  { text: 'Saint Kitts and Nevis', value: 'KN' },
  { text: 'Saint Lucia', value: 'LC' },
  { text: 'Saint Pierre and Miquelon', value: 'PM' },
  { text: 'Saint Vincent and the Grenadines', value: 'VC' },
  { text: 'Samoa', value: 'WS' },
  { text: 'San Marino', value: 'SM' },
  { text: 'Sao Tome and Principe', value: 'ST' },
  { text: 'Saudi Arabia', value: 'SA' },
  { text: 'Senegal', value: 'SN' },
  { text: 'Serbia and Montenegro', value: 'CS' },
  { text: 'Seychelles', value: 'SC' },
  { text: 'Sierra Leone', value: 'SL' },
  { text: 'Singapore', value: 'SG' },
  { text: 'Slovakia', value: 'SK' },
  { text: 'Slovenia', value: 'SI' },
  { text: 'Solomon Islands', value: 'SB' },
  { text: 'Somalia', value: 'SO' },
  { text: 'South Africa', value: 'ZA' },
  { text: 'South Georgia and the South Sandwich Islands', value: 'GS' },
  { text: 'Spain', value: 'ES' },
  { text: 'Sri Lanka', value: 'LK' },
  { text: 'Sudan', value: 'SD' },
  { text: 'Suriname', value: 'SR' },
  { text: 'Svalbard and Jan Mayen', value: 'SJ' },
  { text: 'Swaziland', value: 'SZ' },
  { text: 'Sweden', value: 'SE' },
  { text: 'Switzerland', value: 'CH' },
  { text: 'Syrian Arab Republic', value: 'SY' },
  { text: 'Taiwan, Province of China', value: 'TW' },
  { text: 'Tajikistan', value: 'TJ' },
  { text: 'Tanzania, United Republic of', value: 'TZ' },
  { text: 'Thailand', value: 'TH' },
  { text: 'Timor-Leste', value: 'TL' },
  { text: 'Togo', value: 'TG' },
  { text: 'Tokelau', value: 'TK' },
  { text: 'Tonga', value: 'TO' },
  { text: 'Trinidad and Tobago', value: 'TT' },
  { text: 'Tunisia', value: 'TN' },
  { text: 'Turkey', value: 'TR' },
  { text: 'Turkmenistan', value: 'TM' },
  { text: 'Turks and Caicos Islands', value: 'TC' },
  { text: 'Tuvalu', value: 'TV' },
  { text: 'Uganda', value: 'UG' },
  { text: 'Ukraine', value: 'UA' },
  { text: 'United Arab Emirates', value: 'AE' },
  { text: 'United Kingdom', value: 'GB' },
  { text: 'United States Minor Outlying Islands', value: 'UM' },
  { text: 'Uruguay', value: 'UY' },
  { text: 'Uzbekistan', value: 'UZ' },
  { text: 'Vanuatu', value: 'VU' },
  { text: 'Venezuela', value: 'VE' },
  { text: 'Viet Nam', value: 'VN' },
  { text: 'Virgin Islands, British', value: 'VG' },
  { text: 'Virgin Islands, U.S.', value: 'VI' },
  { text: 'Wallis and Futuna', value: 'WF' },
  { text: 'Western Sahara', value: 'EH' },
  { text: 'Yemen', value: 'YE' },
  { text: 'Zambia', value: 'ZM' },
  { text: 'Zimbabwe', value: 'ZW' },
];

function SelectedPlanCard({
  tier,
  price,
  billingCycle,
  features = [],
  tag,
  accent,
}) {
  if (!tier && !price && features.length === 0) {
    return null;
  }

  return (
    <Box sx={cardStyles.card}>
      {accent ? (
        <Box
          data-card-accent
          aria-hidden
          sx={{
            ...cardStyles.accentCircle,
            backgroundColor: accent.color,
            ...accent.position,
          }}
        />
      ) : null}
      {tag ? <Typography sx={cardStyles.tag}>{tag}</Typography> : null}
      <Box>
        <Typography sx={cardStyles.tier}>{tier || '--'}</Typography>
        <Typography sx={cardStyles.price}>
          {price}
          {billingCycle ? (
            <Typography component="span" sx={cardStyles.priceSuffix}>
              /{billingCycle}
            </Typography>
          ) : null}
        </Typography>
      </Box>

      <Box sx={cardStyles.list}>
        {features.map((feature, idx) => (
          <Typography component="div" key={idx} sx={cardStyles.listItem}>
            <Check sx={{ fontSize: '18px', marginTop: '2px' }} />
            <span>{feature}</span>
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const FreemiumStep2 = ({
  planData,
  goBack,
  hideBack,
  setSelectedPlanFinal,
  handleContinueStep3,
  handleContinueStep4,
  isUpgrading,
  setIsUpgrading,
}) => {
  const {
    accountDetails,
    currentUserInfo,
    isFreemium,
    currentPlan,
    type,
    asLoggedUserDetails,
  } = useAccount();
  const boardOwner = useBoardOwner(accountDetails.contacts);
  const location = useLocation();
  const { t } = useTranslation();
  const recurly = useRecurly();

  const stackColumns = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const planType = planData?.type || 'bronze';
  const billingCycle = formatBillingCycle(
    planData?.recurlyPlanCodeTerm,
    planData?.salesforceValues?.billingCycle
  );
  const tierName = planData?.salesforceValues?.tier || 'AdvisoryCloud';
  const upgradeTitle = `Upgrade to ${tierName} Membership`;
  const priceDisplay = formatPrice(planData?.recurlyPlanCodePrice);
  const selectedFeatures = buildFeatures(
    planData || {},
    planFeatureFactories[planType] || []
  );
  const planTag = planTagMap[planType] || null;
  const planAccent = planAccentMap[planType];

  const [snackMessage, setSnackMessage] = useState('');
  const [snackbarSuccess, setSnackbarSuccess] = useState(false);
  const [snackbarError, setSnackbarError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const [model, setModel] = useState(INITIAL_MODEL);
  const [applePaySupported, setApplePaySupported] = useState(true);
  const [applePayError, setApplePayError] = useState('');

  const [upgradeOptions, setUpgradeOptions] = useState(
    planData?.recurlyPlanCode || ''
  );
  const [selectedSalesforceValues, setSelectedSalesforceValues] = useState(
    planData?.salesforceValues || {}
  );

  const shallowEqual = (a = {}, b = {}) => {
    if (a === b) return true;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (let i = 0; i < aKeys.length; i++) {
      const k = aKeys[i];
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  useEffect(() => {
    const newCode = planData?.recurlyPlanCode || '';
    if (newCode && newCode !== upgradeOptions) {
      setUpgradeOptions(newCode);
    }
    const newSF = planData?.salesforceValues || {};
    if (!shallowEqual(newSF, selectedSalesforceValues)) {
      setSelectedSalesforceValues(newSF);
    }
  }, [planData]);

  const mountedRef = useRef(false);
  const raiseError = useRef(() => {});

  const contactId =
    currentUserInfo?.username || currentUserInfo?.email || accountDetails.id;
  const ownerFirstName =
    boardOwner?.givenName || accountDetails?.givenName || 'Company';
  const ownerLastName =
    boardOwner?.surName || accountDetails?.surName || 'Team';

  const onInputChange = (event) => {
    const { name, type } = event.target;
    const value =
      type === 'checkbox' ? event.target.checked : event.target.value;
    setModel((prev) => ({ ...prev, [name]: value }));
  };

  const isFormComplete = () =>
    model.postalCode && model.country && model.acceptTermsOfService;

  function handleCardChange(event) {
    setIsCardValid(event.valid);
  }

  function parseErrorMessage(errorMessage) {
    let [errorType, ...restOfMessage] = errorMessage.split(':');
    let message = restOfMessage.join(':').trim();
    return {
      type: errorType,
      message,
    };
  }

  const sendFSEvent = useCallback(
    (success, errorMessage = '', error = null) => {
      sendFSEventOnUpgrade({
        accountDetails,
        currentPlan,
        asLoggedUserDetails,
        newPlan: {
          recurlyPlanCode: upgradeOptions,
          salesforceValues: {
            ...selectedSalesforceValues,
          },
        },
        source: location?.state?.fromBanner ? 'banner' : 'gate',
        type,
        upgradeType: 'freemium',
        success,
        errorMessage: success ? null : errorMessage,
        error: success ? null : error,
      });
    },
    [
      accountDetails,
      currentPlan,
      asLoggedUserDetails,
      upgradeOptions,
      selectedSalesforceValues,
      location,
      type,
    ]
  );

  const sendFSViewedEvent = useCallback(
    (upgradeAttempted = false) => {
      sendFSEventUpgradeViewed({
        accountDetails,
        currentPlan,
        asLoggedUserDetails,
        currentUserInfo,
        source: location?.state?.fromBanner ? 'banner' : 'gate',
        type,
        upgradeType: 'freemium',
        upgradeAttempted,
      });
    },
    [
      accountDetails,
      currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      location,
      type,
    ]
  );

  const handleUpgradeSuccess = useCallback(async () => {
    const successMessage =
      'Success: Your account has been upgraded, you now have full access to AdvisoryCloud.';
    setSnackMessage(successMessage);
    setSnackbarSuccess(true);
    sendFSEvent(true);
    try {
      await updateAttributesFreemiumUpgrade({
        COMPANY_ID: accountDetails.id,
      });
    } catch (error) {
      console.error('Failed to update company attributes after upgrade', error);
    }
    setSelectedPlanFinal(upgradeOptions);
    if (false) {
      handleContinueStep4();
    } else {
      handleContinueStep3();
    }
  }, [
    accountDetails.id,
    handleContinueStep3,
    handleContinueStep4,
    // option,
    setSelectedPlanFinal,
    upgradeOptions,
    sendFSEvent,
  ]);

  async function upgradeCall() {
    sendFSViewedEvent(true);
    if (!isUpgrading) {
      setIsUpgrading(true);
      setLoading(true);

      const recurlyForm = {
        first_name: ownerFirstName,
        last_name: ownerLastName,
        postal_code: model.postalCode,
        country: model.country,
      };

      recurly.token(recurlyForm, (err, token) => {
        if (err) {
          const errMessage =
            'There was an error tokenizing the card. Please try again.';
          setSnackMessage(errMessage);
          setSnackbarError(true);
          sendFSEvent(false, errMessage, err);
          setLoading(false);
          setIsUpgrading(false);
          return;
        }

        freemiumUpgradeCompany({
          companyContactId: contactId,
          companyId: accountDetails.id,
          upgradePlanCode: upgradeOptions,
          recurlyBillingToken: token.id,
        })
          .then(async (response) => {
            if (response && response.data) {
              await handleUpgradeSuccess();
              setModel(INITIAL_MODEL);
            } else {
              throw new Error('Upgrade failed, no data returned.');
            }
          })
          .catch((e) => {
            console.error('Upgrade error', e);
            let errMessage = '';
            try {
              const err = JSON.parse(e.errors[0].message);
              if (
                err.code === errorCodes.recurlyError ||
                err.code === errorCodes.validationError
              ) {
                let parsedError = parseErrorMessage(err.error);
                errMessage = parsedError.message;
              } else if (err.code === errorCodes.salesforceError) {
                let parsedError = parseErrorMessage(err.error);
                setSnackMessage(parsedError.message);
                setSnackbarSuccess(true);
                setLoading(false);
                setIsUpgrading(false);
                sendFSEvent(true);
                return;
              } else {
                errMessage =
                  'There was an unexpected error in the application. Please try again later.';
              }
            } catch (error) {
              errMessage =
                'There was an unexpected error in the application. Please try again later.';
            }
            setSnackMessage(errMessage);
            setSnackbarError(true);
            sendFSEvent(false, errMessage, e);
          })
          .finally(() => {
            setLoading(false);
            setIsUpgrading(false);
          });
      });
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    raiseError.current = (msg) => {
      if (!mountedRef.current) {
        const el = document.getElementById('apple-pay-error-company');
        if (el) el.innerText = msg;
        setApplePayError('');
      } else {
        const el = document.getElementById('apple-pay-error-company');
        if (el) el.innerText = '';
        setApplePayError(String(msg || 'Something went wrong'));
      }
      setLoading(false);
      setIsUpgrading(false);
    };
  }, [setIsUpgrading]);

  useEffect(() => {
    const applePayInitKey = `${AP_INIT_KEY}_${upgradeOptions}`;
    if (!recurly || !planData?.recurlyPlanCodePrice || !upgradeOptions) {
      return;
    }
    if (window[applePayInitKey]) {
      return;
    }

    window[applePayInitKey] = true;
    let cleanupButtonListener = null;

    try {
      const applePay = recurly.ApplePay({
        country: 'US',
        currency: 'USD',
        label: 'AdvisoryCloud Upgrade',
        total: Number(planData.recurlyPlanCodePrice),
        requiredBillingContactFields: ['email'],
        callbacks: {
          onPaymentAuthorized: async function (event) {
            try {
              setLoading(true);
              setIsUpgrading(true);
              sendFSViewedEvent(true);

              const recurlyToken = event?.payment?.recurlyToken?.id;
              if (!recurlyToken) {
                raiseError.current('Unable to obtain payment token.');
                return;
              }

              const res = await freemiumUpgradeCompany({
                companyContactId: contactId,
                companyId: accountDetails.id,
                upgradePlanCode: upgradeOptions,
                recurlyBillingToken: recurlyToken,
              });

              if (res && res.data) {
                await handleUpgradeSuccess();
                setModel(INITIAL_MODEL);
              } else {
                throw new Error('Upgrade failed, no data returned.');
              }
            } catch (err) {
              let errMessage = '';
              try {
                const parsed = JSON.parse(err.errors?.[0]?.message);
                if (
                  parsed?.code === errorCodes.recurlyError ||
                  parsed?.code === errorCodes.validationError
                ) {
                  const details = parseErrorMessage(parsed.error);
                  errMessage = details.message;
                } else if (parsed?.code === errorCodes.salesforceError) {
                  const details = parseErrorMessage(parsed.error);
                  setSnackMessage(details.message);
                  setSnackbarSuccess(true);
                  sendFSEvent(true);
                  setModel(INITIAL_MODEL);
                  return;
                } else {
                  errMessage =
                    'There was an unexpected error in the application. Please try again later.';
                }
              } catch (_) {
                errMessage =
                  'There was an unexpected error in the application. Please try again later.';
              }
              raiseError.current(errMessage);
              sendFSEvent(false, errMessage, err);
            } finally {
              setLoading(false);
              setIsUpgrading(false);
            }
          },
        },
      });

      applePay.ready(function () {
        const button = document.getElementById('applePayFreemiumCompany');
        if (!button) return;
        const handler = function () {
          setApplePayError('');
          applePay.begin();
        };
        button.addEventListener('click', handler);
        cleanupButtonListener = () =>
          button.removeEventListener('click', handler);
      });

      applePay.on('error', function (err) {
        console.error('[ApplePay Company] error', err);
        if (
          err?.name === 'apple-pay-not-supported' ||
          err?.code === 'apple-pay-not-supported'
        ) {
          setApplePaySupported(false);
        }
        setApplePayError(err?.message || 'Apple Pay error');
        setLoading(false);
        setIsUpgrading(false);
      });
    } catch (err) {
      console.error('[ApplePay Company] init error', err);
      setApplePayError(err?.message || 'Apple Pay error');
      setApplePaySupported(false);
    }

    return () => {
      if (cleanupButtonListener) cleanupButtonListener();
      delete window[applePayInitKey];
    };
  }, [
    recurly,
    planData?.recurlyPlanCodePrice,
    upgradeOptions,
    accountDetails.id,
    contactId,
    handleUpgradeSuccess,
    sendFSEvent,
    sendFSViewedEvent,
    setIsUpgrading,
  ]);

  const disableUpgradeButton =
    loading || isUpgrading || !isCardValid || !isFormComplete();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        minHeight: { xxs: '100%', sm: 'auto' },
      }}
    >
      <Box
        id="companyFreemiumUpgradeModal"
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '980px',
          backgroundColor: '#FFFFFF',
          borderRadius: { xxs: 0, sm: '20px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: '38px',
          gap: '25px',
        }}
      >
        {!hideBack && (
          <Box
            sx={{
              position: 'absolute',
              left: { xxs: '20px', sm: '40px' },
              top: { xxs: '24px', sm: '32px' },
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            <img
              onClick={goBack}
              src={Arrow}
              alt="Go back"
              style={{ width: '18px' }}
            />
          </Box>
        )}

        <Box sx={{ mt: { xxs: '12px', sm: 0 } }}>
          <AdvisoryCloudLogo width={190} height={34} />
        </Box>

        <Typography
          sx={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: { xxs: '26px', sm: '28px' },
            color: '#232B35',
            lineHeight: { xxs: '34px', sm: '40px' },
            // maxWidth: '520px',
          }}
        >
          {upgradeTitle}
        </Typography>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: stackColumns ? 'column-reverse' : 'row',
            justifyContent: 'center',
            alignItems: stackColumns ? 'center' : 'flex-start',
            gap: { xxs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: { xxs: '100%', sm: 'auto' },
              mb: stackColumns ? 3 : 0,
            }}
          >
            <SelectedPlanCard
              tier={tierName}
              price={priceDisplay}
              billingCycle={billingCycle}
              features={selectedFeatures}
              tag={planTag}
              accent={planAccent}
            />
          </Box>

          <Box
            sx={{
              width: { xxs: '100%', sm: '360px' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Select
                value={model.country}
                name="country"
                onChange={onInputChange}
                sx={{ height: '48px', width: '100%' }}
              >
                {countries.map((country, index) => (
                  <MenuItem key={index} value={country.value}>
                    {country.text}
                  </MenuItem>
                ))}
              </Select>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="City"
                  name="city"
                  value={model.city}
                  onChange={onInputChange}
                  sx={{
                    '& .MuiInputBase-root': { height: '48px' },
                    width: '33%',
                  }}
                />
                <TextField
                  placeholder="State"
                  name="state"
                  value={model.state}
                  onChange={onInputChange}
                  sx={{
                    '& .MuiInputBase-root': { height: '48px' },
                    width: '33%',
                  }}
                />
                <TextField
                  placeholder="Zip"
                  name="postalCode"
                  value={model.postalCode}
                  onChange={onInputChange}
                  sx={{
                    '& .MuiInputBase-root': { height: '48px' },
                    width: '33%',
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ marginTop: '-6px', width: '100%' }}>
              <CardElement onChange={handleCardChange} />
            </Box>

            <Box
              sx={{
                width: '100%',
                // mt: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                // gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <Checkbox
                  name="acceptTermsOfService"
                  checked={model.acceptTermsOfService}
                  onChange={onInputChange}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                    paddingLeft: 0,
                    paddingRight: '6px',
                  }}
                />
                <Typography
                  sx={{ fontSize: '11px', lineHeight: '16px', mt: '8px' }}
                >
                  I have read and accept the AdvisoryCloud{' '}
                  <a
                    href="https://advisorycloud.com/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0077ff' }}
                  >
                    Terms of Service
                  </a>
                </Typography>
              </Box>

              <Button
                color="primary"
                size="large"
                variant="contained"
                disabled={disableUpgradeButton}
                onClick={upgradeCall}
                sx={{
                  width: '100%',
                  height: '52px',
                  borderRadius: '40px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>{applePaySupported ? 'Upgrade Now' : 'Upgrade'}</>
                )}
              </Button>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#646D7A',
                  lineHeight: '18px',
                  fontWeight: 400,
                }}
              >
                By clicking "Upgrade" your free plan will conclude and your card
                will be charged.
              </Typography>
              {applePaySupported && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <Divider
                    textAlign="center"
                    sx={{
                      mb: 1,
                      '&::before, &::after': {
                        borderTop: '1px solid #646D7A',
                        width: '100%',
                      },
                      '& .MuiDivider-wrapper': {
                        px: 2,
                        fontSize: '10px',
                        color: '#646D7A',
                        textTransform: 'none',
                      },
                    }}
                  >
                    or use Express Checkout
                  </Divider>
                  <Button
                    type="button"
                    id="applePayFreemiumCompany"
                    variant="contained"
                    sx={{
                      bgcolor: '#000000',
                      color: 'white',
                      borderRadius: '40px',
                      '&:hover': { bgcolor: '#000000' },
                      '&:disabled': {
                        bgcolor: 'rgba(45, 41, 41, 1)',
                        color: 'white',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      },

                      fontSize: '18px',
                      fontWeight: 700,
                      padding: '10px 0',
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <>
                        Buy with&nbsp;
                        <AppleIcon
                          sx={{
                            fontSize: 24,
                            verticalAlign: 'middle',
                            mr: '6px',
                            mt: '-4px',
                          }}
                        />
                        Pay
                      </>
                    )}
                  </Button>
                  {applePayError && (
                    <Typography
                      color="error"
                      textAlign="center"
                      fontSize="12px"
                    >
                      {applePayError}
                    </Typography>
                  )}
                  <Typography
                    color="error"
                    textAlign="center"
                    fontSize="12px"
                    id="apple-pay-error-company"
                  ></Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <ACSnackbar
        id="companyFreemiumUpgradeSuccessSnackbar"
        style={{ marginTop: isFreemium ? '50px' : '0px' }}
        open={snackbarSuccess}
        text={snackMessage ? t(snackMessage) : ''}
        severity={SnackbarType.SUCCESS}
        onClose={() => setSnackbarSuccess(false)}
        autoHideDuration={3000}
      />
      <ACSnackbar
        style={{ marginTop: isFreemium ? '50px' : '0px' }}
        open={snackbarError}
        text={snackMessage ? t(snackMessage) : ''}
        severity={SnackbarType.ERROR}
        onClose={() => setSnackbarError(false)}
        autoHideDuration={6000}
      />
    </Box>
  );
};

export default FreemiumStep2;
